import React, { useState, useMemo } from 'react';
import { FileText, Plus, Loader2, Edit2, Trash2, PauseCircle, PlayCircle, Save, X } from 'lucide-react';
import { taxConfigAPI } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../ui/alert-dialog';
import { toast } from 'sonner';

const EMPTY_RATE = { name: '', rate: '', category: '', description: '', is_active: true };

export default function TaxTab({ taxConfig, setTaxConfig, taxPresets, org, loadData }) {
  const [processing, setProcessing] = useState(false);
  const [showTaxPresets, setShowTaxPresets] = useState(false);
  const [showDeactivateTax, setShowDeactivateTax] = useState(false);

  const countryPresets = useMemo(() => taxPresets.filter(p => p.country_code === org?.country), [taxPresets, org?.country]);
  const otherPresets = useMemo(() => taxPresets.filter(p => p.country_code !== org?.country), [taxPresets, org?.country]);

  // Rate editing state
  const [showRateDialog, setShowRateDialog] = useState(false);
  const [editingRateIndex, setEditingRateIndex] = useState(null); // null = adding new
  const [rateForm, setRateForm] = useState(EMPTY_RATE);
  const [deleteRateIndex, setDeleteRateIndex] = useState(null);

  const handleActivateTax = async (preset) => {
    setProcessing(true);
    try {
      const res = await taxConfigAPI.activate({
        country_code: preset.country_code,
        country_name: preset.country_name,
        tax_name: preset.tax_name,
        rates: preset.rates.map(r => ({ ...r, is_active: true })),
        default_rate: preset.default_rate,
        input_account_code: "1170",
        output_account_code: "2300",
      });
      setTaxConfig(res.data);
      setShowTaxPresets(false);
      toast.success(`${preset.tax_name} (${preset.country_name}) activated`);
      await loadData();
    } catch {
      toast.error('Failed to activate tax config');
    } finally {
      setProcessing(false);
    }
  };

  const handleDeactivateTax = async () => {
    setProcessing(true);
    try {
      await taxConfigAPI.deactivate(taxConfig.id);
      setTaxConfig(null);
      setShowDeactivateTax(false);
      toast.success('Tax configuration deactivated');
    } catch {
      toast.error('Failed to deactivate');
    } finally {
      setProcessing(false);
    }
  };

  const updateConfig = async (newRates, newDefaultRate) => {
    if (!taxConfig?.id) return;
    setProcessing(true);
    try {
      const res = await taxConfigAPI.update(taxConfig.id, {
        country_code: taxConfig.country_code,
        country_name: taxConfig.country_name,
        tax_name: taxConfig.tax_name,
        rates: newRates,
        default_rate: newDefaultRate ?? taxConfig.default_rate,
        input_account_code: taxConfig.input_account_code || "1170",
        output_account_code: taxConfig.output_account_code || "2300",
      });
      setTaxConfig(res.data);
      return true;
    } catch {
      toast.error('Failed to update tax configuration');
      return false;
    } finally {
      setProcessing(false);
    }
  };

  // Add / Edit rate
  const openAddRate = () => {
    setEditingRateIndex(null);
    setRateForm({ ...EMPTY_RATE });
    setShowRateDialog(true);
  };

  const openEditRate = (index) => {
    const rate = taxConfig.rates[index];
    setEditingRateIndex(index);
    setRateForm({
      name: rate.name || '',
      rate: rate.rate ?? '',
      category: rate.category || '',
      description: rate.description || '',
      is_active: rate.is_active !== false,
    });
    setShowRateDialog(true);
  };

  const handleSaveRate = async () => {
    if (!rateForm.name.trim()) return toast.error('Rate name is required');
    const rateValue = parseFloat(rateForm.rate);
    if (isNaN(rateValue) || rateValue < 0 || rateValue > 100) return toast.error('Rate must be between 0 and 100');

    const entry = {
      name: rateForm.name.trim(),
      rate: rateValue,
      category: rateForm.category.trim() || null,
      description: rateForm.description.trim() || null,
      is_active: rateForm.is_active,
    };

    let newRates;
    if (editingRateIndex !== null) {
      newRates = [...taxConfig.rates];
      newRates[editingRateIndex] = entry;
    } else {
      newRates = [...taxConfig.rates, entry];
    }

    const ok = await updateConfig(newRates);
    if (ok) {
      setShowRateDialog(false);
      toast.success(editingRateIndex !== null ? 'Tax rate updated' : 'Tax rate added');
    }
  };

  // Delete rate
  const handleDeleteRate = async () => {
    if (deleteRateIndex === null) return;
    const newRates = taxConfig.rates.filter((_, i) => i !== deleteRateIndex);

    // Update default rate if the deleted rate was the default
    let newDefault = taxConfig.default_rate;
    if (taxConfig.rates[deleteRateIndex]?.rate === taxConfig.default_rate && newRates.length > 0) {
      newDefault = newRates.find(r => r.is_active !== false)?.rate || newRates[0].rate;
    }

    const ok = await updateConfig(newRates, newDefault);
    if (ok) {
      setDeleteRateIndex(null);
      toast.success('Tax rate deleted');
    }
  };

  // Suspend / Reactivate rate
  const handleToggleSuspend = async (index) => {
    const newRates = [...taxConfig.rates];
    newRates[index] = { ...newRates[index], is_active: newRates[index].is_active === false ? true : false };
    const ok = await updateConfig(newRates);
    if (ok) {
      toast.success(newRates[index].is_active ? 'Tax rate reactivated' : 'Tax rate suspended');
    }
  };

  return (
    <>
      <Card>
        <CardHeader className="flex flex-row items-start justify-between gap-4">
          <div>
            <CardTitle className="text-base flex items-center gap-2">
              <FileText className="w-5 h-5 text-[#D94E18]" />Tax / VAT Configuration
            </CardTitle>
            <CardDescription>Manage your tax rates for automatic gross/net/tax splitting.</CardDescription>
          </div>
          {!taxConfig ? (
            <Button onClick={() => setShowTaxPresets(true)} className="bg-[#D94E18] hover:bg-[#C2410C]" size="sm" data-testid="setup-tax-btn">
              <Plus className="w-4 h-4 mr-1.5" />Set Up Tax
            </Button>
          ) : (
            <div className="flex gap-2 flex-shrink-0">
              <Button variant="outline" size="sm" onClick={() => setShowTaxPresets(true)} data-testid="change-tax-btn">Change Country</Button>
              <Button variant="outline" size="sm" className="text-red-600 border-red-200 hover:bg-red-50" onClick={() => setShowDeactivateTax(true)} data-testid="deactivate-tax-btn">Deactivate</Button>
            </div>
          )}
        </CardHeader>
        <CardContent>
          {taxConfig ? (
            <div className="space-y-5">
              {/* Config header */}
              <div className="flex items-center gap-3 p-4 bg-emerald-50 border border-emerald-200 rounded-lg">
                <div className="w-10 h-10 bg-emerald-100 rounded-full flex items-center justify-center text-lg font-bold text-emerald-700">{taxConfig.country_code}</div>
                <div>
                  <p className="font-medium text-zinc-900">{taxConfig.country_name} — {taxConfig.tax_name}</p>
                  <p className="text-xs text-zinc-500">Default rate: {taxConfig.default_rate}%</p>
                </div>
                <Badge className="ml-auto bg-emerald-600">Active</Badge>
              </div>

              {/* Tax Rates Table */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <p className="text-xs font-medium text-zinc-500 uppercase tracking-wider">Tax Rates</p>
                  <Button size="sm" variant="outline" onClick={openAddRate} data-testid="add-tax-rate-btn">
                    <Plus className="w-3.5 h-3.5 mr-1.5" />Add Rate
                  </Button>
                </div>
                <div className="border border-zinc-200 rounded-lg divide-y divide-zinc-100">
                  {taxConfig.rates.length === 0 ? (
                    <div className="p-6 text-center text-sm text-zinc-400">No tax rates configured. Add one to get started.</div>
                  ) : (
                    taxConfig.rates.map((rate, i) => {
                      const isSuspended = rate.is_active === false;
                      return (
                        <div
                          key={`${rate.rate}-${rate.name}`}
                          className={`flex items-center justify-between px-4 py-3 ${isSuspended ? 'bg-zinc-50 opacity-60' : ''}`}
                          data-testid={`tax-rate-row-${rate.rate}`}
                        >
                          <div className="flex items-center gap-3 min-w-0">
                            <div className="min-w-0">
                              <div className="flex items-center gap-2">
                                <span className="text-sm font-medium text-zinc-800">{rate.name}</span>
                                {isSuspended && (
                                  <Badge variant="outline" className="text-amber-600 border-amber-300 text-[10px]">Suspended</Badge>
                                )}
                                {rate.rate === taxConfig.default_rate && !isSuspended && (
                                  <Badge variant="outline" className="text-blue-600 border-blue-200 text-[10px]">Default</Badge>
                                )}
                              </div>
                              {rate.description && (
                                <p className="text-xs text-zinc-400 mt-0.5">{rate.description}</p>
                              )}
                            </div>
                          </div>

                          <div className="flex items-center gap-2 flex-shrink-0 ml-4">
                            <Badge variant="outline" className={`font-mono ${isSuspended ? 'text-zinc-400' : ''}`}>
                              {rate.rate}%
                            </Badge>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-8 w-8 p-0"
                              onClick={() => openEditRate(i)}
                              disabled={processing}
                              data-testid={`edit-rate-${i}`}
                            >
                              <Edit2 className="w-3.5 h-3.5 text-zinc-400" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-8 w-8 p-0"
                              onClick={() => handleToggleSuspend(i)}
                              disabled={processing}
                              data-testid={`suspend-rate-${i}`}
                            >
                              {isSuspended ? (
                                <PlayCircle className="w-3.5 h-3.5 text-emerald-500" />
                              ) : (
                                <PauseCircle className="w-3.5 h-3.5 text-amber-500" />
                              )}
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-8 w-8 p-0 text-zinc-400 hover:text-red-500"
                              onClick={() => setDeleteRateIndex(i)}
                              disabled={processing}
                              data-testid={`delete-rate-${i}`}
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>

              {/* Account info */}
              <div className="p-3 bg-zinc-50 rounded-lg text-xs text-zinc-500">
                <p>Accounts: VAT Input <span className="font-mono">{taxConfig.input_account_code}</span> / VAT Output <span className="font-mono">{taxConfig.output_account_code}</span></p>
              </div>
            </div>
          ) : (
            <div className="py-8 text-center text-zinc-400">
              <FileText className="w-8 h-8 mx-auto mb-2 opacity-50" />
              <p className="text-sm mb-1">No tax configuration active</p>
              <p className="text-xs">Transactions will be posted without tax splitting</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add / Edit Rate Dialog */}
      <Dialog open={showRateDialog} onOpenChange={setShowRateDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>{editingRateIndex !== null ? 'Edit Tax Rate' : 'Add Tax Rate'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label>Name *</Label>
              <Input
                value={rateForm.name}
                onChange={e => setRateForm(prev => ({ ...prev, name: e.target.value }))}
                placeholder="e.g. Standard, Reduced, Zero-rated"
                data-testid="rate-name-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Rate (%) *</Label>
                <Input
                  type="number"
                  step="0.1"
                  min="0"
                  max="100"
                  value={rateForm.rate}
                  onChange={e => setRateForm(prev => ({ ...prev, rate: e.target.value }))}
                  placeholder="8.1"
                  className="font-mono"
                  data-testid="rate-value-input"
                />
              </div>
              <div className="space-y-1.5">
                <Label>Category</Label>
                <Input
                  value={rateForm.category}
                  onChange={e => setRateForm(prev => ({ ...prev, category: e.target.value }))}
                  placeholder="standard"
                  data-testid="rate-category-input"
                />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Description</Label>
              <Input
                value={rateForm.description}
                onChange={e => setRateForm(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Most goods & services"
                data-testid="rate-description-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRateDialog(false)}>Cancel</Button>
            <Button
              onClick={handleSaveRate}
              disabled={processing}
              className="bg-[#D94E18] hover:bg-[#C2410C] text-white"
              data-testid="save-rate-btn"
            >
              {processing ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <Save className="w-4 h-4 mr-1.5" />}
              {editingRateIndex !== null ? 'Update' : 'Add'} Rate
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Rate Confirmation */}
      <AlertDialog open={deleteRateIndex !== null} onOpenChange={() => setDeleteRateIndex(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Tax Rate?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete the &quot;{taxConfig?.rates?.[deleteRateIndex]?.name}&quot; rate ({taxConfig?.rates?.[deleteRateIndex]?.rate}%)? This won&apos;t affect existing transactions.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteRate}
              disabled={processing}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-delete-rate-btn"
            >
              Delete Rate
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Tax Presets Dialog */}
      <Dialog open={showTaxPresets} onOpenChange={setShowTaxPresets}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Select Your Country</DialogTitle></DialogHeader>
          <p className="text-xs text-zinc-500 -mt-1 mb-2">
            {org?.country ? `Based on your organization settings, we recommend ${org.country}.` : 'Choose the country for tax configuration.'}
          </p>
          <div className="space-y-2 py-2 max-h-[400px] overflow-y-auto">
            {org?.country && taxPresets.filter(p => p.country_code === org.country).map(preset => (
              <button key={`rec-${preset.country_code}`} onClick={() => handleActivateTax(preset)} disabled={processing} className="w-full flex items-center gap-3 p-4 border-2 border-[#D94E18] rounded-lg bg-orange-50 hover:bg-orange-100 transition-all text-left" data-testid="tax-preset-recommended">
                <div className="w-10 h-10 bg-[#D94E18]/10 rounded-full flex items-center justify-center text-sm font-bold text-[#D94E18] flex-shrink-0">{preset.country_code}</div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="font-medium text-sm">{preset.country_name} — {preset.tax_name}</p>
                    <Badge className="bg-[#D94E18] text-white text-[10px]">Recommended</Badge>
                  </div>
                  <p className="text-xs text-zinc-500 truncate">{preset.rates.map(r => `${r.name} ${r.rate}%`).join(', ')}</p>
                </div>
                <Badge variant="outline" className="font-mono flex-shrink-0">{preset.default_rate}%</Badge>
              </button>
            ))}
            {otherPresets.map(preset => (
              <button key={preset.country_code} onClick={() => handleActivateTax(preset)} disabled={processing} className="w-full flex items-center gap-3 p-4 border border-zinc-200 rounded-lg hover:border-[#D94E18] hover:bg-orange-50 transition-all text-left" data-testid={`tax-preset-${preset.country_code}`}>
                <div className="w-10 h-10 bg-zinc-100 rounded-full flex items-center justify-center text-sm font-bold text-zinc-600 flex-shrink-0">{preset.country_code}</div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm">{preset.country_name} — {preset.tax_name}</p>
                  <p className="text-xs text-zinc-500 truncate">{preset.rates.map(r => `${r.name} ${r.rate}%`).join(', ')}</p>
                </div>
                <Badge variant="outline" className="font-mono flex-shrink-0">{preset.default_rate}%</Badge>
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Deactivate Tax Dialog */}
      <AlertDialog open={showDeactivateTax} onOpenChange={setShowDeactivateTax}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Deactivate Tax Configuration?</AlertDialogTitle>
            <AlertDialogDescription>New transactions will no longer have tax splitting. Existing data is unchanged.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeactivateTax} disabled={processing} className="bg-red-600 hover:bg-red-700" data-testid="confirm-deactivate-tax-btn">Deactivate</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
